import Component from "@base/Component.js";
import config from "@/config.js";
import t from "@/services/translationService.js";
import { lockScroll, unlockScroll, enterFullscreen, exitFullscreen } from "@/services/scrollLock.js";

/**
 * Fullscreen image slideshow overlay. Mount to document.body.
 * props: { galleryId | imageDir, images: [{name}], startIndex, alt }
 *
 * `galleryId` is the common case — one path segment, encoded here. `imageDir`
 * is for callers whose images live under a multi-segment path (gear items are
 * served from "showcase/<id>"): pass it already encoded, because encoding it
 * here would turn its separators into %2F and miss the route entirely.
 */
export default class GallerySlideshow extends Component {
  constructor(props) {
    super(props);
    this.state = { index: props.startIndex || 0 };
  }

  #src(i) {
    const img = this.props.images[i] || {};
    // Accept a full URL (src) or an image name (built from the caller's dir).
    if (img.src) return img.src;
    const dir = this.props.imageDir || encodeURIComponent(this.props.galleryId);
    return `${config.imageBase}/${dir}/${encodeURIComponent(img.name)}`;
  }

  render() {
    const { images } = this.props;
    const i = this.state.index;
    return `
      <div class="slideshow">
        <button class="slideshow__close" aria-label="${t.t("slideshow.close")}">&times;</button>
        <button class="slideshow__nav slideshow__prev" aria-label="${t.t("slideshow.prev")}">&#8249;</button>
        <img class="slideshow__img" src="${this.#src(i)}" alt="${Component.escape(this.props.alt || "")}" />
        <button class="slideshow__nav slideshow__next" aria-label="${t.t("slideshow.next")}">&#8250;</button>
        <div class="slideshow__counter">${i + 1} / ${images.length}</div>
      </div>`;
  }

  #go(delta) {
    const n = this.props.images.length;
    this.setState({ index: (this.state.index + delta + n) % n });
  }

  onMounted() {
    lockScroll();
    // Target documentElement, not this.el: setState() replaces this.el on
    // every re-render (prev/next), and removing the current fullscreen
    // element from the document auto-exits fullscreen per spec. Requesting
    // on the already-fullscreen documentElement again is a no-op resolve,
    // so this is safe to call on every onMounted, not just the first.
    enterFullscreen(document.documentElement);
    this.listen(this.$(".slideshow__close"), "click", () => this.unmount());
    this.listen(this.$(".slideshow__prev"), "click", () => this.#go(-1));
    this.listen(this.$(".slideshow__next"), "click", () => this.#go(1));
    // Keyboard nav (bound once on document; re-bound each render via listen()).
    this.listen(document, "keydown", (e) => {
      if (e.key === "Escape") this.unmount();
      else if (e.key === "ArrowLeft") this.#go(-1);
      else if (e.key === "ArrowRight") this.#go(1);
    });
  }

  onBeforeUnmount() {
    unlockScroll();
    exitFullscreen();
  }
}
